"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"

import { cn } from "@/lib/utils"
import { docCategories, type Doc } from "@/content/marketing/docs-categories"

// The sidebar only needs these fields — not the full doc body. Keeping the prop
// narrow avoids serializing every doc's `content` into the client bundle.
export type DocNavItem = Pick<Doc, "slug" | "title" | "category" | "order">

/**
 * Left-hand navigation for the /docs section. Groups docs by category (in the
 * order declared in docCategories) and sorts each group by the doc's `order`
 * field, so editors control sidebar placement from frontmatter. Highlights the
 * doc matching the current route the way a real docs site does.
 */
export function DocsSidebar({ docs }: { docs: DocNavItem[] }) {
  const pathname = usePathname()

  const grouped = docCategories.map((category) => ({
    ...category,
    docs: docs
      .filter((doc) => doc.category === category.id)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999)),
  }))

  return (
    <nav aria-label="Docs navigation" className="space-y-6 text-sm">
      {grouped.map((category) =>
        category.docs.length === 0 ? null : (
          <div key={category.id}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {category.label}
            </p>
            <ul className="space-y-0.5">
              {category.docs.map((doc) => {
                const href = `/docs/${doc.slug}`
                const isActive = pathname === href
                return (
                  <li key={doc.slug}>
                    <Link
                      href={href}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "block rounded-md px-3 py-1.5 transition-colors",
                        isActive
                          ? "bg-primary/10 font-medium text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {doc.title}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      )}
    </nav>
  )
}
